import { useEffect, useState } from "react";
import CustomModal from "../Modals";
import GotIt from "../Modals/GotIt";
import BuyCoinsWithRubies from "./BuyCoinsWithRubies";
import { useUser } from "../../Data/UserContext";
import { trackProductSelected } from "../../utils/frontendTrackingEvents";
import SaleCountdown from "./SaleCountdown";
import { currentPrice, getSoftOffers, OffersTypes } from "./api/offers";

const SHOP_PAGE_PATH = "/shop";

/** Skeleton card matching one "Buy coins" offer (icon, amount, button) */
function SoftOfferCardSkeleton() {
  return (
    <div className="flex flex-col justify-center items-center gap-1 relative px-2 py-3 bg-white w-full min-w-0">
      <div className="w-10 h-10 bg-gray-200 animate-pulse shrink-0" aria-hidden />
      <div className="h-5 w-14 bg-gray-200 animate-pulse shrink-0" aria-hidden />
      <div className="h-10 w-[100px] bg-gray-200 animate-pulse shrink-0" aria-hidden />
    </div>
  );
}

function SoftOffersSkeleton() {
  return (
    <div className="flex w-[100%] items-center justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
      {[1, 2, 3].map((i) => (
        <SoftOfferCardSkeleton key={i} />
      ))}
    </div>
  );
}

interface SoftOffersProps {
  setIsAnimatingCoins: (isAnimatingCoins: boolean) => void;
  onPlayCoinsSound?: () => void;
  totalRubies: number;
  updateUserAmount: (
    offerId: string,
    offerType: string,
    meta?: { pay_amount: number; receive_amount: number; offer_name?: string }
  ) => void;
}
export default function SoftOffers({
  setIsAnimatingCoins,
  onPlayCoinsSound,
  totalRubies,
  updateUserAmount,
}: SoftOffersProps) {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [softOffers, setSoftOffers] = useState<OffersTypes[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<OffersTypes | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noMoreRubies, setNoMoreRubies] = useState(false);

  useEffect(() => {
    fetchSoftOffers();
  }, []);

  async function fetchSoftOffers({ silent = false }: { silent?: boolean } = {}) {
    if (!silent) setLoading(true);
    try {
      const softData = await getSoftOffers();
      setSoftOffers(softData);
    } catch (error) {
      if (!silent) setError(error as string);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  const refreshAfterSaleEnds = () => void fetchSoftOffers({ silent: true });

  if (loading) return <SoftOffersSkeleton />;
  if (error)
    return <h1 className="text-red font-anton">Something went wrong...</h1>;

  function openOfferModal(item: OffersTypes) {
    const price = currentPrice(item);
    if (price > totalRubies) {
      setNoMoreRubies(true);
      return;
    }
    trackProductSelected({
      user_id: user?.userId,
      product_id: item.id,
      product_type: "coin_pack",
      product_name: item.offer_name?.trim() || `x${item.receive_amount}_coins`,
      price,
      ...(item.on_sale && { original_price: item.pay_amount, discount_pct: item.sale_percent }),
      currency: "RUBIES",
      pathname: SHOP_PAGE_PATH,
    });
    setSelectedOffer(item);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setSelectedOffer(null);
  }

  function closeNoRubiesModal() {
    setNoMoreRubies(false);
  }

  return (
    <>
      <div className="flex w-[100%] items-center justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
        {softOffers.map((item: OffersTypes) => {
          const onSale = Boolean(item.on_sale && item.sale_percent);
          return (
            <div
              key={item.id}
              className={`flex flex-col justify-center items-center gap-1 relative overflow-hidden px-2 py-3 bg-white w-full ${
                onSale ? "ring-2 ring-inset ring-red" : ""
              }`}
            >
              {onSale ? (
                <div className="absolute p-[2px] text-[13px] w-[120px] bg-red top-[15px] right-[-40px] rotate-[45deg] text-center font-anton text-white">
                  −{item.sale_percent}%
                </div>
              ) : null}
              <img
                className="w-[40px] h-[40px]"
                src="/assets/currency-coin.png"
                alt="coin"
              />
              <p className="text-gold font-anton font-[400] text-[20px]">
                x{item.receive_amount >= 1000 ? `${(item.receive_amount / 1000).toFixed(0)}K` : item.receive_amount}
              </p>
              {onSale ? (
                <p className="flex items-center gap-[4px] font-anton text-[13px] leading-none">
                  <span className="flex items-center gap-0.5 text-gray-400 line-through" aria-label={`Was ${item.pay_amount} rubies`}>
                    <img className="w-[11px] h-[11px] opacity-60" src="/assets/currency-ruby.png" alt="" />
                    {item.pay_amount}
                  </span>
                  <span className="bg-red px-[4px] py-[1px] text-[11px] uppercase text-white">
                    Save {item.pay_amount - currentPrice(item)}
                  </span>
                </p>
              ) : null}
              <button
                onClick={() => openOfferModal(item)}
                className="font-anton text-white uppercase font-[400] text-[14px] flex flex-row items-center justify-center gap-[2px] bg-lime whitespace-nowrap w-[100px] h-[40px] text-center font-[400]"
              >
                <span className="font-anton font-[14px] text-[#1B062F]">GET FOR</span>
                <img
                  className="w-[15px] h-[15px] inline-block"
                  src="/assets/currency-ruby.png"
                  alt="ruby"
                />
                <span className="font-anton font-[14px] text-[#1B062F]">{currentPrice(item)}</span>
              </button>
              {onSale && item.sale_ends_at ? (
                <SaleCountdown endsAt={item.sale_ends_at} onExpire={refreshAfterSaleEnds} className="text-red" />
              ) : null}
            </div>
          );
        })}
        <GotIt
          isOpen={noMoreRubies}
          onRequestClose={closeNoRubiesModal}
          contentLabel="No Rubies"
          children={
            <div className="flex justify-center items-center p-4">
              <p className="font-[600] text-white text-center text-[20px]">
                NOT ENOUGH RUBIES TO MAKE THAT PURCHASE. GET RUBIES AND TRY
                AGAIN.
              </p>
            </div>
          }
        />
      </div>
      <CustomModal
        isOpen={open}
        onRequestClose={closeModal}
        contentLabel="BUY"
        children={
          selectedOffer && (
            <BuyCoinsWithRubies
              receive_amount={selectedOffer?.receive_amount}
              pay_amount={currentPrice(selectedOffer)}
              original_pay_amount={selectedOffer.on_sale ? selectedOffer.pay_amount : undefined}
              coinId={selectedOffer?.id}
              offerType={selectedOffer.offer_type}
              offerName={selectedOffer?.offer_name}
              closeModal={closeModal}
              setIsAnimatingCoins={setIsAnimatingCoins}
              onPlayCoinsSound={onPlayCoinsSound}
              updateUserAmount={updateUserAmount}
            />
          )
        }
      />
    </>
  );
}
